import { useState } from "react";

function americanToDecimal(a) {
  return 1 + (a > 0 ? a / 100 : 100 / Math.abs(a));
}

export default function EVCalc() {
  const [odds, setOdds] = useState("+250");
  const [prob, setProb] = useState(32);
  const [stake, setStake] = useState(100);

  const a = parseInt(odds.replace("+", ""), 10);
  const valid = !isNaN(a) && a !== 0 && prob > 0 && prob < 100 && stake > 0;
  let ev = 0, kelly = 0, dec = 0, implied = 0, payout = 0;
  if (valid) {
    dec = americanToDecimal(a);
    const p = prob / 100;
    const b = dec - 1;
    ev = stake * b * p - stake * (1 - p);
    kelly = Math.max((b * p - (1 - p)) / b, 0);
    implied = (1 / dec) * 100;
    payout = stake * dec;
  }

  const Field = ({ label, children }) => (
    <div>
      <label className="text-xs uppercase tracking-wider text-neutral-400 font-semibold">{label}</label>
      {children}
    </div>
  );

  return (
    <div className="space-y-5 max-w-3xl">
      <div className="fade-up">
        <h1 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight">EV <span className="gold-text">Calculator</span></h1>
        <p className="text-neutral-500 text-sm mt-1">Expected value and Kelly criterion bet sizing.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-5 fade-up-1">
        <div className="card-surface rounded-xl p-6 space-y-4">
          <Field label="American Odds">
            <input value={odds} onChange={(e) => setOdds(e.target.value)} data-testid="ev-odds-input"
              className="mt-1 w-full bg-[#0a0a0a] border border-white/15 focus:border-[#a855f7] rounded px-3 py-2.5 text-white font-data outline-none transition-colors duration-200" placeholder="+250 or -110" />
          </Field>
          <Field label={`Win Probability — ${prob}%`}>
            <input type="range" min="1" max="99" value={prob} onChange={(e) => setProb(Number(e.target.value))} data-testid="ev-prob-input"
              className="mt-2 w-full accent-[#a855f7]" />
          </Field>
          <Field label="Stake ($)">
            <input type="number" min="1" value={stake} onChange={(e) => setStake(Number(e.target.value))} data-testid="ev-stake-input"
              className="mt-1 w-full bg-[#0a0a0a] border border-white/15 focus:border-[#a855f7] rounded px-3 py-2.5 text-white font-data outline-none transition-colors duration-200" />
          </Field>
        </div>

        <div className="card-gold border border-[#a855f7]/30 rounded-xl p-6 gold-glow">
          <h3 className="font-display text-lg font-bold uppercase text-[#a855f7]">Results</h3>
          {valid ? (
            <div className="mt-4 space-y-4">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-neutral-500">Expected Value</div>
                <div className={`font-data text-4xl font-bold ${ev >= 0 ? "text-[#22c55e]" : "text-red-400"}`} data-testid="ev-result">
                  {ev >= 0 ? "+" : "-"}${Math.abs(ev).toFixed(2)}
                </div>
                <div className="text-xs text-neutral-500 mt-1">{ev >= 0 ? "Positive EV — this bet has long-term value." : "Negative EV — the book has the edge here."}</div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm border-t border-white/10 pt-4">
                <div><div className="text-neutral-500 text-[11px] uppercase">Kelly Size</div><div className="font-data font-bold text-[#a855f7]" data-testid="ev-kelly">{(kelly * 100).toFixed(1)}% <span className="text-neutral-500 font-normal">(${(kelly * stake).toFixed(0)})</span></div></div>
                <div><div className="text-neutral-500 text-[11px] uppercase">Half Kelly</div><div className="font-data font-bold">{(kelly * 50).toFixed(1)}%</div></div>
                <div><div className="text-neutral-500 text-[11px] uppercase">Implied Prob</div><div className="font-data">{implied.toFixed(1)}%</div></div>
                <div><div className="text-neutral-500 text-[11px] uppercase">Payout</div><div className="font-data">${payout.toFixed(2)}</div></div>
                <div><div className="text-neutral-500 text-[11px] uppercase">Decimal Odds</div><div className="font-data">{dec.toFixed(2)}</div></div>
                <div><div className="text-neutral-500 text-[11px] uppercase">Your Edge</div><div className={`font-data ${prob - implied > 0 ? "text-[#22c55e]" : "text-red-400"}`}>{(prob - implied).toFixed(1)}%</div></div>
              </div>
            </div>
          ) : (
            <p className="text-neutral-500 text-sm mt-4">Enter valid odds (e.g., +250 or -110), probability and stake.</p>
          )}
        </div>
      </div>
    </div>
  );
}
